import { useEffect, useRef } from "react";
import {
  Eye,
  FlipHorizontal2,
  FlipVertical2,
  Gauge,
  MicOff,
  Shield,
  Star,
  Timer,
  User,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  setTablePhoneMainTable,
  setTablePhoneTables,
  toggleTablePhoneTable,
} from "../../utils/tablePhoneCoverage";
import VideocamIcon from "@mui/icons-material/Videocam";
import {
  EVAL_VIDEO_CAPTURE,
  WEB_EVAL_FPS_OPTIONS,
  isWebEvalCameraSelected,
  webEvalCameraButtonLabel,
  webEvalCameraLensMark,
  webEvalCameraOptionLabel,
  webEvalCameraSetupLabel,
  webEvalHasWideLens,
  webEvalZoomWideLens,
} from "../../utils/evalVideoEncoding";
import {
  AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS,
  VIDEO_QUALITY_OPTIONS,
} from "../../utils/liveTrainingSettings";
import {
  DEFAULT_CAMERA_ZOOM,
  MAX_FALLBACK_CAMERA_ZOOM,
  formatCameraZoomLabel,
  logicalZoomRange,
  nextPinchZoom,
  pinchDistance,
  wideZoomMin,
} from "../../utils/cameraTrackZoom";
import { useLiveTrainingEvalRecorder } from "../../hooks/useLiveTrainingEvalRecorder";
import { useTableReplayRing } from "../../hooks/useTableReplayRing";
import { OffTableChronoCameraHud } from "./OffTableChronoCameraHud";
import "./LiveTrainingVideoCalibrationOverlay.css";

function previewTransform(flipH, flipV, zoom = 1, hardwareZoom = false) {
  const parts = [];
  if (flipH) parts.push("scaleX(-1)");
  if (flipV) parts.push("scaleY(-1)");
  // When hardware zoom is not applied, show digital zoom in the preview so
  // pinch / buttons always have visible feedback (and match the recorded crop).
  if (!hardwareZoom && zoom > 1.001) {
    parts.push(`scale(${zoom})`);
  }
  return parts.length ? parts.join(" ") : "none";
}

/** Immediate CSS paint during pinch — skips React so zoom stays fluid. */
function paintPreviewTransform(el, flipH, flipV, zoom) {
  if (!el) return;
  el.style.transform = previewTransform(flipH, flipV, zoom, false);
}

function statusLabel(status, t, captureMode) {
  if (status === "recording") {
    return captureMode === "kot"
      ? t("liveTrainingKotMatchRecording", "Recording match")
      : t("liveTrainingEvalRecording", "Recording evaluation clip");
  }
  if (status === "uploading") {
    return captureMode === "kot"
      ? t("liveTrainingKotMatchUploading", "Uploading match video")
      : t("liveTrainingEvalUploading", "Uploading evaluation clip");
  }
  if (status === "uploaded") {
    return captureMode === "kot"
      ? t("liveTrainingKotMatchUploaded", "Match video uploaded")
      : t("liveTrainingEvalUploaded", "Evaluation clip uploaded");
  }
  if (status === "saved_on_phone") {
    return t(
      "liveTrainingEvalSavedOnPhone",
      "Saved on this phone. It uploads when the connection is back."
    );
  }
  if (status === "error") {
    return t(
      "liveTrainingEvalCameraError",
      "Camera unavailable. Check browser permissions."
    );
  }
  return t(
    "liveTrainingEvalPreviewHint",
    "Pinch or use ± to zoom. Adjust FPS, quality, and clip length here."
  );
}

function qualityMark(quality) {
  const q = String(quality || "low").toLowerCase();
  if (q === "high") return "H";
  if (q === "medium") return "M";
  return "L";
}

const MAIN_TABLE_HOLD_MS = 450;

/**
 * Tables this camera films. Tap toggles a table; hold makes it the main
 * table (seated players for evaluation clips).
 */
function TableCoverageStrip({
  tableOptions,
  covered,
  mainTable,
  disabled,
  onChange,
  t,
}) {
  const holdRef = useRef({ timer: 0, fired: false });
  const coveredSet = new Set(covered);
  const allOn =
    tableOptions.length > 1 && tableOptions.every((n) => coveredSet.has(n));

  const clearHold = () => {
    window.clearTimeout(holdRef.current.timer);
    holdRef.current.timer = 0;
  };

  useEffect(() => clearHold, []);

  let caption;
  if (!covered.length) {
    caption = t(
      "liveTrainingCameraTablesHintEmpty",
      "Tap every table this camera can see to record it."
    );
  } else if (covered.length === 1) {
    caption = t(
      "liveTrainingCameraTablesHintOne",
      "Recording table {{number}}. Tap more tables if a wide lens sees them.",
      { number: covered[0] }
    );
  } else {
    caption = t(
      "liveTrainingCameraTablesHintMany",
      "Recording {{count}} tables. The video is shared with the players on each of them.",
      { count: covered.length }
    );
  }

  return (
    <div className="lt-eval-calib__tables">
      <div
        className="lt-eval-calib__dock-row lt-eval-calib__dock-row--tables"
        role="group"
        aria-label={t("liveTrainingCameraTables", "Tables this camera records")}
      >
        <span className="lt-eval-calib__tables-label" aria-hidden>
          {t("liveTrainingCameraTablesShort", "Tables")}
        </span>
        {tableOptions.map((n) => {
          const on = coveredSet.has(n);
          const isMain = on && covered.length > 1 && n === mainTable;
          const label = t("table_with_number", { number: n }) || `Table ${n}`;
          return (
            <button
              key={n}
              type="button"
              className={`lt-eval-calib__chip lt-eval-calib__table${
                on ? " is-on" : ""
              }${isMain ? " is-main" : ""}`}
              aria-pressed={on}
              aria-label={
                isMain
                  ? `${label} · ${t("liveTrainingCameraMainTable", "main table")}`
                  : label
              }
              title={t(
                "liveTrainingCameraTableChipTitle",
                "Tap to film this table. The video is shared with its players. Hold to make it the main table."
              )}
              disabled={disabled}
              onPointerDown={() => {
                clearHold();
                holdRef.current.fired = false;
                holdRef.current.timer = window.setTimeout(() => {
                  holdRef.current.fired = true;
                  onChange((prev) => setTablePhoneMainTable(prev, n));
                  navigator.vibrate?.(12);
                }, MAIN_TABLE_HOLD_MS);
              }}
              onPointerUp={clearHold}
              onPointerLeave={clearHold}
              onPointerCancel={clearHold}
              onContextMenu={(e) => e.preventDefault()}
              onClick={() => {
                if (holdRef.current.fired) {
                  holdRef.current.fired = false;
                  return;
                }
                onChange((prev) => toggleTablePhoneTable(prev, n));
              }}
            >
              {isMain ? (
                <Star
                  className="lt-eval-calib__table-star"
                  size={10}
                  strokeWidth={2.6}
                  aria-hidden
                />
              ) : null}
              <span>{n}</span>
            </button>
          );
        })}
        {tableOptions.length > 1 ? (
          <button
            type="button"
            className={`lt-eval-calib__chip lt-eval-calib__tables-all${
              allOn ? " is-on" : ""
            }`}
            aria-pressed={allOn}
            disabled={disabled}
            onClick={() =>
              onChange((prev) =>
                setTablePhoneTables(prev, allOn ? [] : tableOptions)
              )
            }
          >
            {allOn
              ? t("liveTrainingCameraTablesNone", "None")
              : t("liveTrainingCameraTablesAll", "All")}
          </button>
        ) : null}
      </div>
      <p className="lt-eval-calib__tables-caption">{caption}</p>
    </div>
  );
}

export function LiveTrainingEvalCaptureHost({
  enabled = false,
  coachDevice = false,
  tsId = null,
  exercise = null,
  exerciseStateId = null,
  awaitingManualSequenceStart = false,
  awaitingAutoSequenceStart = false,
  viewerPlayerId = null,
  viewerTableNumber = null,
  viewerSide = null,
  tablePhone = false,
  tablePlayers = null,
  /** Session table numbers this phone may film (empty hides the picker). */
  tableOptions = [],
  /** Tables this phone films now (main included). */
  tablePhoneTables = [],
  tablePhoneMainTable = null,
  /** (update: (clientSettings) => clientSettings) => void */
  onTablePhoneCoverageChange = null,
  kotSnapshot = null,
  isLandscape = false,
  t = (k, d) => d || k,
  onUiChange,
  socket = null,
  userId = null,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const stageRef = useRef(null);
  const zoomLiveRef = useRef(DEFAULT_CAMERA_ZOOM);
  const pinchActiveRef = useRef(false);
  const flipLiveRef = useRef({ flipH: false, flipV: false });
  const pinchRef = useRef({
    pointers: new Map(),
    startDistance: 0,
    startZoom: DEFAULT_CAMERA_ZOOM,
    wheelCommitTimer: 0,
  });
  const rec = useLiveTrainingEvalRecorder({
    enabled,
    coachDevice,
    tsId,
    exercise,
    exerciseStateId,
    awaitingManualSequenceStart,
    awaitingAutoSequenceStart,
    viewerPlayerId,
    viewerTableNumber,
    viewerSide,
    tablePhone,
    tablePlayers,
    tablePhoneTables,
    kotSnapshot,
    videoRef,
    canvasRef,
    socket,
  });

  useTableReplayRing({
    active: Boolean(rec.replayWarm),
    getStream: rec.getMediaStream,
    getCaptureTransform: rec.getReplayCaptureTransform,
    isEvalRecording: rec.isEvalRecording,
    socket,
    tsId,
    teId: exercise?.te_id || null,
    tableNumber: viewerTableNumber,
    userId,
    playerId: viewerPlayerId,
  });

  // Coach hides Replay on this table while the evaluation video records.
  // Heartbeat so a coach who opens the dashboard mid-capture still learns it.
  const evalRecordingNow = Boolean(rec.recording);
  const evalTeId = exercise?.te_id || null;
  useEffect(() => {
    if (!socket || !tsId || viewerTableNumber == null) return undefined;
    const announce = (recording) =>
      socket.emit("tableEvalRecording", {
        ts_id: tsId,
        te_id: evalTeId,
        table_number: viewerTableNumber,
        recording,
      });
    if (!evalRecordingNow) return undefined;
    announce(true);
    const timer = window.setInterval(() => announce(true), 5000);
    return () => {
      window.clearInterval(timer);
      announce(false);
    };
  }, [socket, tsId, evalTeId, viewerTableNumber, evalRecordingNow]);

  useEffect(() => {
    onUiChange?.({
      enabled: rec.recordingEnabled,
      recording: rec.recording,
      uploading: rec.uploading,
      openCalibration: rec.openCalibration,
      closeCalibration: rec.closeCalibration,
      cameraError: rec.cameraError,
      debugSnapshot: rec.debugSnapshot,
      facingMode: rec.facingMode,
      cameraReady: rec.cameraReady,
    });
  }, [
    onUiChange,
    rec.recordingEnabled,
    rec.recording,
    rec.uploading,
    rec.openCalibration,
    rec.closeCalibration,
    rec.cameraError,
    rec.debugSnapshot,
    rec.facingMode,
    rec.cameraReady,
  ]);

  flipLiveRef.current = { flipH: rec.flipH, flipV: rec.flipV };

  // Seed live zoom when calibration opens. Do not sync from React `rec.zoom`
  // on every render — it lags pinch by a frame and snaps the preview on release.
  useEffect(() => {
    if (!rec.calibrationOpen) return;
    zoomLiveRef.current = rec.zoom;
    paintPreviewTransform(
      previewRef.current,
      rec.flipH,
      rec.flipV,
      rec.zoom
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-seed on open
  }, [rec.calibrationOpen]);

  useEffect(() => {
    const preview = previewRef.current;
    const src = videoRef.current;
    if (!preview || !src) return;
    if (preview.srcObject !== src.srcObject) {
      preview.srcObject = src.srcObject;
    }
    preview.muted = true;
    preview.playsInline = true;
    preview.play?.().catch(() => {});
  }, [rec.calibrationOpen, rec.cameraReady, rec.cameraSwitching, rec.stream]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!rec.calibrationOpen || !stage) return undefined;
    const pinch = pinchRef.current;
    const rangeForPinch = () =>
      logicalZoomRange(rec.zoomRange) || {
        min: DEFAULT_CAMERA_ZOOM,
        max: MAX_FALLBACK_CAMERA_ZOOM,
      };

    const storePointer = (event) => {
      pinch.pointers.set(event.pointerId, {
        clientX: event.clientX,
        clientY: event.clientY,
      });
    };

    const paintLiveZoom = (nextZoom) => {
      zoomLiveRef.current = nextZoom;
      const { flipH, flipV } = flipLiveRef.current;
      paintPreviewTransform(previewRef.current, flipH, flipV, nextZoom);
    };

    const onPointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      storePointer(event);
      try {
        stage.setPointerCapture?.(event.pointerId);
      } catch {
        /* ignore */
      }
      if (pinch.pointers.size === 2) {
        const [a, b] = [...pinch.pointers.values()];
        const dist = pinchDistance(a, b);
        if (!(dist > 0)) return;
        pinchActiveRef.current = true;
        pinch.startDistance = dist;
        pinch.startZoom = zoomLiveRef.current;
      }
    };
    const onPointerMove = (event) => {
      if (!pinch.pointers.has(event.pointerId)) return;
      storePointer(event);
      if (pinch.pointers.size !== 2 || !(pinch.startDistance > 0)) return;
      event.preventDefault();
      const [a, b] = [...pinch.pointers.values()];
      const next = nextPinchZoom(
        pinch.startZoom,
        pinch.startDistance,
        pinchDistance(a, b),
        rangeForPinch()
      );
      paintLiveZoom(next);
      // Digital preview only while calibrating — optical zoom is applied when
      // the overlay closes. Applying hardware here stacks with CSS scale.
      rec.setCaptureZoom(next, { persist: false, applyHardware: false });
    };
    const onPointerUp = (event) => {
      pinch.pointers.delete(event.pointerId);
      try {
        stage.releasePointerCapture?.(event.pointerId);
      } catch {
        /* ignore */
      }
      if (pinch.pointers.size < 2) {
        pinch.startDistance = 0;
        const next = rec.setCaptureZoom(zoomLiveRef.current, {
          persist: true,
          applyHardware: false,
        });
        paintLiveZoom(next);
        pinchActiveRef.current = false;
      }
    };
    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.06 : 1 / 1.06;
      const next = rec.setCaptureZoom(zoomLiveRef.current * factor, {
        persist: false,
        applyHardware: false,
      });
      paintLiveZoom(next);
      window.clearTimeout(pinch.wheelCommitTimer);
      pinch.wheelCommitTimer = window.setTimeout(() => {
        const committed = rec.setCaptureZoom(zoomLiveRef.current, {
          persist: true,
          applyHardware: false,
        });
        paintLiveZoom(committed);
      }, 120);
    };
    const onDblClick = () => {
      pinchActiveRef.current = false;
      const next = rec.setCaptureZoom(DEFAULT_CAMERA_ZOOM, {
        persist: true,
        applyHardware: false,
      });
      paintLiveZoom(next);
    };

    stage.addEventListener("pointerdown", onPointerDown);
    stage.addEventListener("pointermove", onPointerMove, { passive: false });
    stage.addEventListener("pointerup", onPointerUp);
    stage.addEventListener("pointercancel", onPointerUp);
    stage.addEventListener("lostpointercapture", onPointerUp);
    stage.addEventListener("wheel", onWheel, { passive: false });
    stage.addEventListener("dblclick", onDblClick);
    return () => {
      stage.removeEventListener("pointerdown", onPointerDown);
      stage.removeEventListener("pointermove", onPointerMove);
      stage.removeEventListener("pointerup", onPointerUp);
      stage.removeEventListener("pointercancel", onPointerUp);
      stage.removeEventListener("lostpointercapture", onPointerUp);
      stage.removeEventListener("wheel", onWheel);
      stage.removeEventListener("dblclick", onDblClick);
      window.clearTimeout(pinch.wheelCommitTimer);
      pinch.pointers.clear();
      pinchActiveRef.current = false;
    };
  }, [rec.calibrationOpen, rec.setCaptureZoom, rec.zoomRange]);

  if (!enabled) return null;

  const locked = Boolean(rec.captureLocked) || rec.recording;
  const showTables =
    typeof onTablePhoneCoverageChange === "function" &&
    Array.isArray(tableOptions) &&
    tableOptions.length > 0;
  const zoomStep = () => {
    const range = logicalZoomRange(rec.zoomRange);
    const span = Math.max(0.25, (range.max - range.min) / 12);
    return span;
  };
  const selectedCamera = (rec.cameras || []).find((option) =>
    isWebEvalCameraSelected(option, {
      deviceId: rec.deviceId,
      facingMode: rec.facingMode,
    })
  );
  const zoomWideLens = webEvalZoomWideLens(
    selectedCamera,
    rec.cameras,
    wideZoomMin(rec.zoomRange)
  );
  const selectWideZoom = async (wide) => {
    const next = await rec.setCaptureWideZoom(wide);
    zoomLiveRef.current = next;
    paintPreviewTransform(previewRef.current, rec.flipH, rec.flipV, next);
  };
  const renderLens = (option, { active, label, onClick }) => {
    const mark = webEvalCameraLensMark(option);
    const isWide = option.lens === "ultrawide";
    return (
      <button
        key={option.id}
        type="button"
        className={`lt-eval-calib__lens${active ? " is-on" : ""}${
          isWide ? " lt-eval-calib__lens--wide" : ""
        }`}
        onClick={onClick}
        aria-pressed={active}
        aria-label={label}
        disabled={rec.recording || rec.cameraSwitching}
        title={label}
      >
        <span className="lt-eval-calib__lens-mark">
          {mark === "F" && !isWide ? (
            <User size={16} strokeWidth={2.2} aria-hidden />
          ) : (
            mark
          )}
        </span>
        <span className="lt-eval-calib__lens-name">
          {webEvalCameraSetupLabel(option, t)}
        </span>
      </button>
    );
  };

  const railLabel = (text, Icon = null) => (
    <span className="lt-eval-calib__rail-label">
      {Icon ? <Icon size={10} strokeWidth={2.4} aria-hidden /> : null}
      {text}
    </span>
  );

  return (
    <>
      <video
        ref={videoRef}
        className="lt-eval-capture__hidden-video"
        muted
        playsInline
        autoPlay
        webkit-playsinline="true"
      />
      <canvas
        ref={canvasRef}
        className="lt-eval-capture__hidden-canvas"
        width={EVAL_VIDEO_CAPTURE.width}
        height={EVAL_VIDEO_CAPTURE.height}
      />
      {rec.recordingEnabled && rec.cameraReady && rec.micError ? (
        <div className="lt-eval-mic-warning" role="alert">
          <MicOff size={18} strokeWidth={2.2} aria-hidden />
          <span className="lt-eval-mic-warning__text">
            {rec.micError === "NotAllowedError"
              ? t(
                  "liveTrainingEvalMicBlocked",
                  "Microphone blocked: videos record without sound. Allow the microphone for this site in the browser settings."
                )
              : t(
                  "liveTrainingEvalMicMissing",
                  "Microphone off: videos record without sound."
                )}
          </span>
          <button
            type="button"
            className="lt-eval-mic-warning__btn"
            onClick={() => void rec.enableMicrophone()}
          >
            {t("liveTrainingEvalMicEnable", "Enable microphone")}
          </button>
        </div>
      ) : null}
      {rec.calibrationOpen ? (
        <div
          className={`lt-eval-calib${isLandscape ? " lt-eval-calib--landscape" : ""}${
            showTables ? " lt-eval-calib--tables" : ""
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="lt-eval-calib-title"
        >
          <div
            ref={stageRef}
            className={`lt-eval-calib__stage${
              rec.cameraSwitching ? " lt-eval-calib__stage--switching" : ""
            }`}
          >
            <video
              ref={previewRef}
              className="lt-eval-calib__preview"
              muted
              playsInline
              autoPlay
              style={{
                // Always digital in the calib UI so pinch is 1:1 with fingers.
                // Optical zoom is re-applied when closing the overlay.
                // Prefer the live pinch value so React lag cannot snap the scale.
                transform: previewTransform(
                  rec.flipH,
                  rec.flipV,
                  zoomLiveRef.current || rec.zoom,
                  false
                ),
                willChange: "transform",
              }}
            />
            {(zoomLiveRef.current || rec.zoom) > 1.02 ? (
              <span className="lt-eval-calib__zoom-pill" aria-live="polite">
                {formatCameraZoomLabel(zoomLiveRef.current || rec.zoom)}
              </span>
            ) : null}
            {rec.recording ? (
              <span className="lt-eval-calib__rec-pill" aria-live="polite">
                <span className="lt-eval-calib__rec-dot" />
                {t("recording", "REC")}
              </span>
            ) : null}
            <OffTableChronoCameraHud
              socket={socket}
              teId={exercise?.te_id}
              tsgId={exercise?.tsg_id}
              active={Boolean(exercise?.te_id)}
            />
          </div>

          <header className="lt-eval-calib__top">
            <div className="lt-eval-calib__title-row">
              <VideocamIcon fontSize="small" />
              <h2 id="lt-eval-calib-title" className="lt-eval-calib__title">
                {t("liveTrainingVideoCalibration", "Video calibration")}
              </h2>
              <span className="lt-eval-calib__meta-chip" title={t("liveTrainingRecordingResolution", "Resolution")}>
                {rec.recordingResolution || "720p"}
              </span>
            </div>
            <button
              type="button"
              className="lt-eval-calib__icon-btn"
              onClick={rec.closeCalibration}
              aria-label={t("close", "Close")}
              title={t("close", "Close")}
            >
              <X size={20} strokeWidth={2.2} />
            </button>
          </header>

          {showTables ? (
            <TableCoverageStrip
              tableOptions={tableOptions}
              covered={tablePhoneTables || []}
              mainTable={tablePhoneMainTable}
              disabled={rec.recording}
              onChange={onTablePhoneCoverageChange}
              t={t}
            />
          ) : null}

          <p className="lt-eval-calib__hint">
            {statusLabel(rec.status, t, rec.captureMode)}
            {rec.captureLocked
              ? ` · ${t("liveTrainingCaptureLockedByCoach", "Coach locked capture settings")}`
              : ""}
          </p>

          <div
            className="lt-eval-calib__chrome"
            aria-label={t("liveTrainingVideoCalibration", "Video calibration")}
          >
            <div
              className="lt-eval-calib__edge lt-eval-calib__edge--left"
              role="group"
              aria-label={t("zoom", "Zoom")}
            >
              <div className="lt-eval-calib__rail">
                {railLabel(t("zoom", "Zoom"))}
                <div className="lt-eval-calib__dock-row lt-eval-calib__dock-row--zoom">
                  <button
                    type="button"
                    className="lt-eval-calib__icon-btn"
                    onClick={() => {
                      const next = rec.setCaptureZoom(
                        (zoomLiveRef.current || rec.zoom) - zoomStep(),
                        { persist: true, applyHardware: false }
                      );
                      zoomLiveRef.current = next;
                      paintPreviewTransform(
                        previewRef.current,
                        rec.flipH,
                        rec.flipV,
                        next
                      );
                    }}
                    aria-label={t("zoomOut", "Zoom out")}
                    title={t("zoomOut", "Zoom out")}
                  >
                    <ZoomOut size={18} strokeWidth={2.2} />
                  </button>
                  <button
                    type="button"
                    className="lt-eval-calib__zoom-reset"
                    onClick={() => {
                      const next = rec.setCaptureZoom(DEFAULT_CAMERA_ZOOM, {
                        persist: true,
                        applyHardware: false,
                      });
                      zoomLiveRef.current = next;
                      paintPreviewTransform(
                        previewRef.current,
                        rec.flipH,
                        rec.flipV,
                        next
                      );
                    }}
                    title={t("resetZoom", "Reset zoom")}
                  >
                    {formatCameraZoomLabel(zoomLiveRef.current || rec.zoom)}
                  </button>
                  <button
                    type="button"
                    className="lt-eval-calib__icon-btn"
                    onClick={() => {
                      const next = rec.setCaptureZoom(
                        (zoomLiveRef.current || rec.zoom) + zoomStep(),
                        { persist: true, applyHardware: false }
                      );
                      zoomLiveRef.current = next;
                      paintPreviewTransform(
                        previewRef.current,
                        rec.flipH,
                        rec.flipV,
                        next
                      );
                    }}
                    aria-label={t("zoomIn", "Zoom in")}
                    title={t("zoomIn", "Zoom in")}
                  >
                    <ZoomIn size={18} strokeWidth={2.2} />
                  </button>
                </div>
              </div>

              <div className="lt-eval-calib__rail">
                {railLabel(t("liveTrainingCameraPosition", "View"))}
                <div
                  className="lt-eval-calib__dock-row"
                  role="group"
                  aria-label={t("liveTrainingCameraPosition", "Camera position")}
                >
                  {[
                    {
                      id: "spectator",
                      icon: Eye,
                      label: t("liveTrainingCameraSpectator", "Spectator"),
                    },
                    {
                      id: "referee",
                      icon: Shield,
                      label: t("liveTrainingCameraReferee", "Referee"),
                    },
                  ].map((option) => {
                    const active =
                      String(rec.cameraView || "spectator") === option.id;
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={`lt-eval-calib__icon-btn${
                          active ? " lt-eval-calib__icon-btn--active" : ""
                        }`}
                        onClick={() => rec.setCameraView?.(option.id)}
                        aria-pressed={active}
                        disabled={rec.recording}
                        title={option.label}
                        aria-label={option.label}
                      >
                        <Icon size={18} strokeWidth={2.2} aria-hidden />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="lt-eval-calib__rail">
                {railLabel(t("liveTrainingEvalFps", "FPS"), Gauge)}
                <div
                  className="lt-eval-calib__dock-row lt-eval-calib__dock-row--stack"
                  role="group"
                  aria-label={t("liveTrainingEvalFps", "FPS")}
                >
                  {WEB_EVAL_FPS_OPTIONS.map((rate) => {
                    const active = rec.fps === rate;
                    return (
                      <button
                        key={rate}
                        type="button"
                        className={`lt-eval-calib__chip${
                          active ? " is-on" : ""
                        }`}
                        onClick={() => rec.setCaptureFps(rate)}
                        aria-pressed={active}
                        disabled={locked}
                        title={`${rate} FPS`}
                        aria-label={`${rate} FPS`}
                      >
                        <span>{rate}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div
              className="lt-eval-calib__edge lt-eval-calib__edge--right"
              role="group"
              aria-label={t("liveTrainingEvalCamera", "Camera")}
            >
              <div className="lt-eval-calib__rail">
                {railLabel(t("liveTrainingEvalCamera", "Lens"))}
                <div
                  className={`lt-eval-calib__dock-row lt-eval-calib__dock-row--lenses${
                    webEvalHasWideLens(rec.cameras) || zoomWideLens
                      ? " lt-eval-calib__dock-row--lenses-wide"
                      : ""
                  }`}
                >
                  {(rec.cameras || []).flatMap((option) => {
                    const selected = isWebEvalCameraSelected(option, {
                      deviceId: rec.deviceId,
                      facingMode: rec.facingMode,
                    });
                    const onZoomWide = Boolean(zoomWideLens) && rec.zoom < 1;
                    const chips = [
                      renderLens(option, {
                        active: selected && !onZoomWide,
                        label: webEvalCameraButtonLabel(option, rec.cameras, t),
                        onClick: () =>
                          selected && onZoomWide
                            ? selectWideZoom(false)
                            : rec.setCaptureCamera(option),
                      }),
                    ];
                    if (selected && zoomWideLens) {
                      chips.push(
                        renderLens(zoomWideLens, {
                          active: onZoomWide,
                          label: `${webEvalCameraOptionLabel(zoomWideLens, t)} (${formatCameraZoomLabel(zoomWideLens.zoom)})`,
                          onClick: () => selectWideZoom(true),
                        })
                      );
                    }
                    return chips;
                  })}
                </div>
              </div>

              <div className="lt-eval-calib__rail">
                {railLabel(t("flip", "Flip"))}
                <div className="lt-eval-calib__dock-row">
                  <button
                    type="button"
                    className={`lt-eval-calib__icon-btn${
                      rec.flipV ? " lt-eval-calib__icon-btn--active" : ""
                    }`}
                    onClick={rec.toggleFlipV}
                    aria-pressed={rec.flipV}
                    aria-label={t("flipVertical", "Flip vertical")}
                    title={t("flipVertical", "Flip vertical")}
                  >
                    <FlipVertical2 size={18} strokeWidth={2.2} />
                  </button>
                  <button
                    type="button"
                    className={`lt-eval-calib__icon-btn${
                      rec.flipH ? " lt-eval-calib__icon-btn--active" : ""
                    }`}
                    onClick={rec.toggleFlipH}
                    aria-pressed={rec.flipH}
                    aria-label={t("flipHorizontal", "Flip horizontal")}
                    title={t("flipHorizontal", "Flip horizontal")}
                  >
                    <FlipHorizontal2 size={18} strokeWidth={2.2} />
                  </button>
                </div>
              </div>

              <div className="lt-eval-calib__rail">
                {railLabel(t("liveTrainingVideoQuality", "Quality"))}
                <div
                  className="lt-eval-calib__dock-row lt-eval-calib__dock-row--stack"
                  role="group"
                  aria-label={t("liveTrainingVideoQuality", "Video quality")}
                >
                  {VIDEO_QUALITY_OPTIONS.map((q) => {
                    const active = String(rec.videoQuality || "low") === q;
                    return (
                      <button
                        key={q}
                        type="button"
                        className={`lt-eval-calib__chip lt-eval-calib__chip--mark${
                          active ? " is-on" : ""
                        }`}
                        onClick={() => rec.setCaptureVideoQuality(q)}
                        aria-pressed={active}
                        disabled={locked}
                        title={t(`liveTrainingQuality_${q}`, q)}
                        aria-label={t(`liveTrainingQuality_${q}`, q)}
                      >
                        <span>{qualityMark(q)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="lt-eval-calib__rail">
                {railLabel(
                  t("liveTrainingAutoevalVideoDurationShort", "Sec"),
                  Timer
                )}
                <div
                  className="lt-eval-calib__dock-row lt-eval-calib__dock-row--stack"
                  role="group"
                  aria-label={t(
                    "liveTrainingAutoevalVideoDuration",
                    "Auto-evaluation video duration"
                  )}
                >
                  {AUTOEVAL_VIDEO_DURATION_SEC_OPTIONS.map((sec) => {
                    const active = Number(rec.windowSec) === sec;
                    return (
                      <button
                        key={sec}
                        type="button"
                        className={`lt-eval-calib__chip${
                          active ? " is-on" : ""
                        }`}
                        onClick={() => rec.setCaptureDurationSec(sec)}
                        aria-pressed={active}
                        disabled={locked}
                        title={`${sec}s`}
                        aria-label={`${sec}s`}
                      >
                        <span>{sec}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default LiveTrainingEvalCaptureHost;
